import { AxiosError } from 'axios';
import { gql, useQuery } from '@apollo/client';
import { restErrorsToForm } from '@unitedtraders/microfrontend-common';
import { VerificationMethod } from '@unitedtraders/utcom-api-sdk';
import { createValidator, tip, TipLevels } from '@unitedtraders/validators';
import { useWithdrawService } from '../../hooks/useWithdrawService';
import { FormValues } from '.';
import { WithdrawConfirmationEmail } from './hooks.types';

const query = gql`
  query WithdrawConfirmationEmail {
    currentUser {
      id
      userInfo {
        email
      }
    }
  }
`;

// tips:
// t('confirmation:Code sent to {{email}}')
// t('confirmation:Enter code from GA')

// errors:
// t('exception:Invalid verification code {code}')
// t('exception:Limit exceeded, execution is blocked now')
// t('exception:attempts_one')
// t('exception:attempts_other')

export const useConfirmation = () => {
  const withdrawService = useWithdrawService();

  const isEmailCode = withdrawService.verificationMethod === VerificationMethod.EMAIL;

  const { data, loading } = useQuery<WithdrawConfirmationEmail>(query, {
    skip: !isEmailCode,
  });

  const validateTips = createValidator<FormValues>({
    code: [
      tip(
        isEmailCode ? 'confirmation:Code sent to {{email}}' : 'confirmation:Enter code from GA',
        { email: data?.currentUser?.userInfo?.email },
        TipLevels.TIP
      ),
    ],
  });

  return {
    loading: isEmailCode && loading,
    validateTips,
    verificationMethod: withdrawService.verificationMethod,
    onSubmit: async (values: FormValues) => {
      try {
        return await withdrawService.confirm(values.code.replace(/[^\d]/g, ''));
      } catch (e: AxiosError | any) {
        const error = restErrorsToForm(e.response?.data || e, 'code');
        if (error.code?.details && 'remainingAttempts' in error.code.details) {
          error.code.details.remainingAttempts += 1; // there is remainingAttempts = 0
          error.code.details.count = error.code.details.remainingAttempts;
        }
        return error;
      }
    },
  };
};
